<template>
  <Teleport to="#app">
    <div
      v-if="should_show_chatbot && is_open"
      class="chatbot-scrim"
      :class="{ 'chatbot-scrim--expanded': is_expanded }"
      @click="onScrimClick"
    />
  </Teleport>

  <Teleport
    to="#app"
    :disabled="!is_expanded"
  >
    <div
      v-if="should_show_chatbot"
      class="chatbot-element"
      :class="{ 'chatbot-element--expanded': is_expanded }"
      :style="is_expanded ? undefined : chatbot_position_style"
    >
      <div
        v-if="is_open"
        class="chatbot-panel"
        :class="{ 'chatbot-panel--expanded': is_expanded }"
        :style="chatbot_panel_style"
      >
        <header
          class="chatbot-panel-header"
          :class="{ 'chatbot-panel-header--expanded': is_full_layout }"
        >
          <div class="chatbot-panel-header-brand">
            <svg-element
              class="chatbot-brand-icon"
              icon="robot"
              size="24"
            />

            <div class="chatbot-panel-header-title nds-text-p-bold">
              {{ chatbot_title }}
            </div>
          </div>

          <div
            v-if="is_full_layout"
            class="chatbot-panel-header-conversation"
          >
            <div class="chatbot-conversation-title nds-text-p-bold">
              {{ current_conversation_title }}
            </div>
          </div>

          <div class="chatbot-panel-header-actions">
            <button
              v-if="!is_full_layout"
              type="button"
              class="chatbot-icon-button"
              :title="$t('new_chat')"
              :aria-label="$t('new_chat')"
              :disabled="is_streaming"
              @click="onNewConversation"
            >
              <svg-element
                icon="pen-writing"
                size="16"
              />
            </button>

            <button
              v-if="show_history_toggle"
              type="button"
              class="chatbot-icon-button"
              :class="{ 'chatbot-icon-button--active': show_conversations }"
              :title="show_conversations ? $t('conversation') : $t('history_title')"
              :aria-label="show_conversations ? $t('conversation') : $t('history_title')"
              @click="onToggleConversations"
            >
              <svg-element
                :icon="show_conversations ? 'chat' : 'history'"
                size="16"
              />
            </button>

            <button
              ref="settings_toggle"
              type="button"
              class="chatbot-icon-button"
              :class="{ 'chatbot-icon-button--active': show_settings }"
              :title="$t('settings_title')"
              :aria-label="$t('settings_title')"
              :aria-expanded="show_settings ? 'true' : 'false'"
              @click="onToggleSettings"
            >
              <svg-element
                icon="settings"
                size="16"
              />
            </button>

            <button
              type="button"
              class="chatbot-icon-button"
              :title="is_expanded ? $t('collapse') : $t('expand')"
              :aria-label="is_expanded ? $t('collapse') : $t('expand')"
              @click="onToggleExpand"
            >
              <svg-element
                :icon="is_expanded ? 'collapse' : 'expand'"
                size="16"
              />
            </button>
          </div>
        </header>

        <div class="chatbot-panel-body">
          <div
            v-if="show_history_panel"
            class="chatbot-history-menu"
            :class="{ 'chatbot-history-menu--sidebar': is_full_layout }"
          >
            <button
              v-if="is_full_layout"
              type="button"
              class="chatbot-new-discussion nds-text-button-small-bold"
              :disabled="is_streaming"
              @click="onNewConversation"
            >
              <svg-element
                icon="plus"
                size="14"
              />

              <span>
                {{ $t('new_chat') }}
              </span>
            </button>

            <div
              v-if="show_history_toggle"
              class="chatbot-history-menu-header"
            >
              <div class="nds-text-p-bold">
                {{ $t('history_title') }}
              </div>

              <button
                v-if="show_history_toggle"
                type="button"
                class="chatbot-icon-button"
                :title="$t('close')"
                :aria-label="$t('close')"
                @click="onCloseConversations"
              >
                <svg-element
                  icon="close"
                  size="16"
                />
              </button>
            </div>

            <div
              v-if="!is_loading_conversations && conversations.length"
              class="chatbot-history-search"
            >
              <svg-element
                class="chatbot-history-search-icon"
                icon="search"
                size="14"
              />

              <input
                v-model="history_search_query"
                type="search"
                class="chatbot-history-search-input nds-text-p-small"
                :placeholder="$t('history_search_placeholder')"
                :aria-label="$t('history_search_placeholder')"
                autocomplete="off"
              >

              <button
                v-if="history_search_query"
                type="button"
                class="chatbot-history-search-clear"
                :title="$t('close')"
                :aria-label="$t('close')"
                @click="clearHistorySearch"
              >
                <svg-element
                  icon="close"
                  size="12"
                />
              </button>
            </div>

            <div
              v-if="is_loading_conversations"
              class="chatbot-loading"
            >
              <progress-circular-element
                indeterminate
                :size="24"
                width="2"
              />
            </div>

            <p
              v-else-if="!conversations.length"
              class="chatbot-history-empty nds-text-p-small"
            >
              {{ $t('no_conversations') }}
            </p>

            <p
              v-else-if="!filtered_conversations.length"
              class="chatbot-history-empty nds-text-p-small"
            >
              {{ $t('no_conversations_found') }}
            </p>

            <ul
              v-else
              class="chatbot-history-list"
            >
              <li
                v-for="conversation in filtered_conversations"
                :key="conversation.id"
                class="chatbot-history-row"
              >
                <button
                  type="button"
                  class="chatbot-history-item"
                  :class="{
                    'chatbot-history-item--active':
                      conversation.id === conversation_id,
                  }"
                  :disabled="is_streaming || is_loading"
                  :aria-label="conversationHistoryLabel(conversation)"
                  @click="onSelectConversation(conversation.id)"
                >
                  <span class="chatbot-history-item-heading">
                    <span
                      v-if="isConversationReadOnly(conversation)"
                      class="chatbot-history-readonly"
                      :title="$t('read_only_conversation')"
                      aria-hidden="true"
                    >
                      <svg-element
                        icon="lock"
                        size="14"
                      />
                    </span>

                    <span class="chatbot-history-item-name nds-text-p-small">
                      {{ conversationName(conversation) }}
                    </span>
                  </span>

                  <span class="chatbot-history-item-date nds-text-tag">
                    {{ formatConversationDate(conversation) }}
                  </span>
                </button>

                <button
                  type="button"
                  class="chatbot-history-delete"
                  :title="$t('delete_conversation')"
                  :aria-label="$t('delete_conversation')"
                  :disabled="is_streaming || is_loading"
                  @click.stop="onRequestDeleteConversation(conversation.id)"
                >
                  <svg-element
                    icon="trash"
                    size="14"
                  />
                </button>
              </li>
            </ul>
          </div>

          <div
            v-click-outside="{
              handler: onClickOutsideSettings,
              include: settingsClickOutsideInclude,
            }"
            class="chatbot-main-area"
          >
            <div class="chatbot-main">
              <div
                ref="messages_container"
                class="chatbot-messages"
              >
                <div
                  v-if="is_loading && !messages.length"
                  class="chatbot-loading"
                >
                  <progress-circular-element
                    indeterminate
                    :size="28"
                    width="2"
                  />
                </div>

                <template v-else>
                  <div
                    v-if="show_welcome"
                    class="chatbot-message chatbot-message--assistant"
                  >
                    <div class="chatbot-message-avatar chatbot-message-avatar--bot">
                      <svg-element
                        icon="robot"
                        size="20"
                      />
                    </div>

                    <div class="chatbot-message-content">
                      <!-- eslint-disable-next-line vue/no-v-html -->
                      <div
                        v-if="opening_statement && should_render_markdown"
                        class="chatbot-message-bubble nds-text-p chatbot-markdown"
                        v-html="renderMarkdown(opening_statement)"
                      />

                      <div
                        v-else-if="opening_statement"
                        class="chatbot-message-bubble nds-text-p chatbot-message-raw"
                      >
                        {{ opening_statement }}
                      </div>

                      <div
                        v-else
                        class="chatbot-message-bubble nds-text-p"
                      >
                        {{ $t('empty_state') }}
                      </div>

                      <div
                        v-if="suggested_questions.length && !is_current_conversation_read_only"
                        class="chatbot-suggestions"
                      >
                        <button
                          v-for="(question, index) in suggested_questions"
                          :key="`suggestion-${index}`"
                          type="button"
                          class="chatbot-suggestion nds-text-p-small"
                          :class="{
                            'chatbot-suggestion--contextual': question.is_contextual,
                          }"
                          :disabled="is_streaming"
                          @click="onSuggestedQuestion(question.label)"
                        >
                          {{ question.label }}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div
                    v-for="message in messages"
                    :key="message.id"
                    class="chatbot-message"
                    :class="`chatbot-message--${message.role}`"
                  >
                    <div
                      v-if="message.role === 'assistant'"
                      class="chatbot-message-avatar chatbot-message-avatar--bot"
                    >
                      <svg-element
                        icon="robot"
                        size="20"
                      />
                    </div>

                    <div
                      v-if="message.role === 'assistant'"
                      class="chatbot-message-content"
                    >
                      <div
                        class="chatbot-message-bubble nds-text-p"
                        :class="{ 'chatbot-markdown': should_render_markdown }"
                      >
                        <!-- eslint-disable-next-line vue/no-v-html -->
                        <div
                          v-if="String(message.content || '').trim() && should_render_markdown"
                          v-html="renderMarkdown(message.content)"
                        />

                        <div
                          v-else-if="String(message.content || '').trim()"
                          class="chatbot-message-raw"
                        >
                          {{ message.content }}
                        </div>

                        <span
                          v-if="message.status === 'streaming' && !String(message.content || '').trim()"
                          class="chatbot-typing"
                        >
                          <span
                            v-if="should_show_loading_steps && display_stream_status"
                            class="chatbot-typing-label"
                          >
                            {{ display_stream_status }}
                          </span>

                          <span
                            class="chatbot-typing-dots"
                            aria-hidden="true"
                          >
                            <span class="chatbot-typing-dot" />
                            <span class="chatbot-typing-dot" />
                            <span class="chatbot-typing-dot" />
                          </span>
                        </span>
                      </div>

                      <div
                        v-if="shouldShowFollowUpQuestions(message)"
                        class="chatbot-suggestions"
                      >
                        <button
                          v-for="(question, index) in message.suggested_questions"
                          :key="`follow-up-${message.id}-${index}`"
                          type="button"
                          class="chatbot-suggestion nds-text-p-small"
                          :disabled="is_streaming"
                          @click="onSuggestedQuestion(question)"
                        >
                          {{ question }}
                        </button>
                      </div>

                      <div
                        v-if="shouldShowCitations(message)"
                        class="chatbot-citations"
                      >
                        <div class="chatbot-citations-title nds-text-tag">
                          {{ $t('sources_title') }}
                        </div>

                        <div class="chatbot-citations-list">
                          <div
                            v-for="resource in message.retriever_resources"
                            :key="`${message.id}-${resource.id}-${resource.position}`"
                            class="chatbot-citation"
                          >
                            <button
                              type="button"
                              class="chatbot-citation-toggle"
                              :aria-expanded="isCitationExpanded(message.id, resource)"
                              @click="toggleCitation(message.id, resource)"
                            >
                              <span class="chatbot-citation-index nds-text-tag">
                                {{ resource.position }}
                              </span>
                              <span class="chatbot-citation-name nds-text-p-small">
                                {{ resource.document_name || $t('sources_untitled') }}
                              </span>
                              <span
                                v-if="resource.dataset_name"
                                class="chatbot-citation-dataset nds-text-tag"
                              >
                                {{ resource.dataset_name }}
                              </span>
                              <span
                                v-if="formatCitationScore(resource.score)"
                                class="chatbot-citation-score nds-text-tag"
                              >
                                {{ formatCitationScore(resource.score) }}
                              </span>
                            </button>

                            <div
                              v-if="isCitationExpanded(message.id, resource) && resource.content"
                              class="chatbot-citation-content nds-text-p-small"
                            >
                              {{ resource.content }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="chatbot-message-content"
                    >
                      <div class="chatbot-message-bubble nds-text-p">
                        {{ message.content }}
                      </div>

                      <span
                        v-if="message.created_at"
                        class="chatbot-message-time nds-text-tag"
                      >
                        {{ formatMessageTime(message.created_at) }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>

              <footer class="chatbot-composer">
                <div class="chatbot-composer-field">
                  <div class="chatbot-composer-input-wrap">
                    <textarea
                      ref="composer_input"
                      v-model="draft_message"
                      class="chatbot-composer-input nds-text-p"
                      :class="{
                        'chatbot-composer-input--read-only':
                          is_current_conversation_read_only,
                      }"
                      :placeholder="composer_placeholder"
                      :aria-label="composer_aria_label"
                      :disabled="is_streaming || is_current_conversation_read_only"
                      :tabindex="is_current_conversation_read_only ? -1 : null"
                      rows="1"
                      @keydown="onComposerKeydown"
                    />

                    <button
                      v-if="is_streaming"
                      type="button"
                      class="chatbot-composer-submit chatbot-composer-submit--stop"
                      :title="$t('stop')"
                      :aria-label="$t('stop')"
                      @click="onStop"
                    >
                      <svg-element
                        icon="stop"
                        size="16"
                      />
                    </button>

                    <button
                      v-else-if="!is_current_conversation_read_only"
                      type="button"
                      class="chatbot-composer-submit"
                      :title="$t('send')"
                      :aria-label="$t('send')"
                      :disabled="!can_send"
                      @click="onSend"
                    >
                      <svg-element
                        icon="arrow-up"
                        size="16"
                      />
                    </button>
                  </div>
                </div>
              </footer>
            </div>

            <div
              v-if="show_settings"
              class="chatbot-settings-menu"
              :class="{ 'chatbot-settings-menu--sidebar': is_expanded }"
            >
              <div class="chatbot-settings-menu-header">
                <div class="chatbot-settings-menu-title nds-text-p-bold">
                  <span>
                    {{ $t('settings_title') }}
                  </span>
                </div>

                <button
                  type="button"
                  class="chatbot-icon-button"
                  :title="$t('close')"
                  :aria-label="$t('close')"
                  @click="onCloseSettings"
                >
                  <svg-element
                    icon="close"
                    size="16"
                  />
                </button>
              </div>

              <section class="chatbot-settings-section">
                <div class="chatbot-settings-section-title nds-text-p-small-bold">
                  {{ $t('settings_font') }}
                </div>

                <label class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_font_family') }}
                  </span>
                  <select
                    class="chatbot-settings-select nds-text-p-small"
                    :value="chatbot_settings.font_family"
                    @change="onFontFamilyChange"
                  >
                    <option
                      v-for="option in font_family_options"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.name }}
                    </option>
                  </select>
                </label>

                <div class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_font_size') }}
                  </span>
                  <div
                    class="chatbot-settings-size-options"
                    role="group"
                    :aria-label="$t('settings_font_size')"
                  >
                    <button
                      v-for="option in font_size_options"
                      :key="option.value"
                      type="button"
                      class="chatbot-settings-size-option nds-text-p-small-bold"
                      :class="{
                        'chatbot-settings-size-option--active':
                          chatbot_settings.font_size === option.value,
                      }"
                      @click="onFontSizeChange(option.value)"
                    >
                      {{ option.name }}
                    </button>
                  </div>
                </div>
              </section>

              <section class="chatbot-settings-section">
                <toggle-element
                  :model-value="chatbot_settings.auto_scroll !== false"
                  :label="$t('settings_auto_scroll')"
                  @update:model-value="onAutoScrollChange"
                />
              </section>

              <section class="chatbot-settings-section">
                <div class="chatbot-settings-section-title nds-text-p-small-bold">
                  {{ $t('settings_enter_action') }}
                </div>

                <div
                  class="chatbot-enter-action"
                  role="radiogroup"
                  :aria-label="$t('settings_enter_action')"
                >
                  <button
                    type="button"
                    class="chatbot-enter-action-label nds-text-p-small"
                    :class="{ 'chatbot-enter-action-label--active': !enter_sends_message }"
                    role="radio"
                    :aria-checked="enter_sends_message ? 'false' : 'true'"
                    @click="onEnterSendsMessageChange(false)"
                  >
                    {{ $t('settings_enter_newline') }}
                  </button>

                  <button
                    type="button"
                    class="chatbot-enter-action-switch"
                    :aria-label="$t('settings_enter_action')"
                    @click="onEnterSendsMessageChange(!enter_sends_message)"
                  >
                    <span
                      class="chatbot-enter-action-knob"
                      :class="{ 'chatbot-enter-action-knob--end': enter_sends_message }"
                    />
                  </button>

                  <button
                    type="button"
                    class="chatbot-enter-action-label chatbot-enter-action-label--end"
                    :class="[
                      'nds-text-p-small',
                      { 'chatbot-enter-action-label--active': enter_sends_message },
                    ]"
                    role="radio"
                    :aria-checked="enter_sends_message ? 'true' : 'false'"
                    @click="onEnterSendsMessageChange(true)"
                  >
                    {{ $t('settings_enter_send') }}
                  </button>
                </div>
              </section>

              <section
                v-if="is_chatbot_admin"
                class="chatbot-settings-section"
              >
                <div class="chatbot-settings-section-title nds-text-p-small-bold">
                  {{ $t('settings_admin') }}
                </div>

                <toggle-element
                  :model-value="chatbot_settings.render_markdown"
                  :label="$t('settings_render_markdown')"
                  @update:model-value="onRenderMarkdownChange"
                />

                <toggle-element
                  :model-value="chatbot_settings.show_detailed_loading_steps !== false"
                  :label="$t('settings_detailed_loading_steps')"
                  @update:model-value="onDetailedLoadingStepsChange"
                />

                <toggle-element
                  :model-value="!chatbot_settings.raw_workflow_steps"
                  :label="$t('settings_formatted_workflow_steps')"
                  :disabled="chatbot_settings.show_detailed_loading_steps === false"
                  @update:model-value="onFormattedWorkflowStepsChange"
                />

                <toggle-element
                  :model-value="Boolean(chatbot_settings.show_follow_up_suggestions)"
                  :label="$t('settings_follow_up_suggestions')"
                  @update:model-value="onFollowUpSuggestionsChange"
                />

                <toggle-element
                  :model-value="Boolean(chatbot_settings.show_sources)"
                  :label="$t('settings_sources')"
                  @update:model-value="onSourcesChange"
                />

                <label class="chatbot-settings-field">
                  <span class="chatbot-settings-field-label nds-text-p-small">
                    {{ $t('settings_api_key') }}

                    <svg-element
                      icon="key"
                      size="12"
                    />
                  </span>
                  <div class="chatbot-settings-api-key">
                    <div class="chatbot-settings-api-key-field">
                      <input
                        v-model="draft_api_key"
                        class="chatbot-settings-input nds-text-p-small"
                        :type="show_api_key ? 'text' : 'password'"
                        autocomplete="off"
                        spellcheck="false"
                        :placeholder="$t('settings_api_key_placeholder')"
                        @keydown.enter.prevent="onSaveApiKey"
                      >
                      <button
                        type="button"
                        class="chatbot-settings-api-key-reveal"
                        :class="{
                          'chatbot-settings-api-key-reveal--visible': show_api_key,
                        }"
                        :title="show_api_key
                          ? $t('settings_api_key_hide')
                          : $t('settings_api_key_show')"
                        :aria-label="show_api_key
                          ? $t('settings_api_key_hide')
                          : $t('settings_api_key_show')"
                        @click="onToggleApiKeyVisibility"
                      >
                        <svg-element
                          :icon="show_api_key ? 'slashed-eye' : 'eye'"
                          size="14"
                        />
                      </button>
                    </div>

                    <button
                      type="button"
                      class="chatbot-settings-api-key-save nds-text-p-small-bold"
                      :disabled="!can_save_api_key"
                      @click="onSaveApiKey"
                    >
                      {{ $t('settings_api_key_save') }}
                    </button>
                  </div>
                </label>
              </section>
            </div>
          </div>
        </div>
      </div>

      <button
        v-if="!is_expanded"
        type="button"
        class="chatbot-bubble"
        :class="{ 'chatbot-bubble--dragging': is_dragging }"
        :title="is_open ? $t('close_chatbot') : $t('open_chatbot')"
        :aria-label="is_open ? $t('close_chatbot') : $t('open_chatbot')"
        @pointerdown="onBubblePointerDown"
        @click="onBubbleClick"
      >
        <svg-element
          v-if="!is_open"
          icon="robot"
          size="38"
        />

        <svg-element
          v-else
          icon="close"
          size="16"
        />
      </button>

      <generic-popup
        v-model="show_delete_conversation_popup"
        :title="$t('delete_conversation_title')"
        width="480"
        :z_index="2400"
        @close="onCloseDeleteConversationPopup"
      >
        <template #content>
          <div class="chatbot-delete-warning">
            <svg-element
              icon="disclaimer"
              size="16"
            />

            <p class="chatbot-delete-warning-text nds-text-p">
              {{ delete_conversation_warning }}
            </p>
          </div>
        </template>

        <template #actions>
          <button-element
            big
            color="blue-o"
            :text="$t('delete_conversation_cancel')"
            @click="show_delete_conversation_popup = false"
          />

          <button-element
            big
            color="red"
            :text="$t('delete_conversation_confirm')"
            @click="onConfirmDeleteConversation"
          />
        </template>
      </generic-popup>
    </div>
  </Teleport>
</template>

<script>
import { useIntercom } from '@homebaseai/vue3-intercom';
import { mapActions, mapGetters } from 'vuex';

import configuration from '@/configurations';
import ButtonElement from '@/components/elements/ButtonElement.vue';
import GenericPopup from '@/components/elements/GenericPopup.vue';
import ProgressCircularElement from '@/components/elements/ProgressCircularElement.vue';
import SvgElement from '@/components/elements/SvgElement.vue';
import ToggleElement from '@/components/elements/ToggleElement.vue';
import {
  applySuggestionTemplate,
  buildPageContext,
  getAccountIdFromRoute,
  parseSuggestedQuestion,
  parseWorkflowNodeStatusLabel,
  pickOpeningStatement,
  suggestionMatchesRoute,
} from '@/utils/chatbotSuggestions';
import {
  CHATBOT_FONT_FAMILY_OPTIONS,
  CHATBOT_FONT_SIZE_OPTIONS,
  getChatbotFontFamilyCss,
} from '@/utils/chatbotSettings';
import { fitChatbotTableColumns, renderMarkdown } from '@/utils/markdown';
import {
  CHATBOT_BUBBLE_SIZE_PX,
  CHATBOT_HEADER_HEIGHT_PX,
  CHATBOT_PANEL_GAP_PX,
  CHATBOT_PANEL_WIDTH_PX,
  clampBubblePosition,
  getBubbleBounds,
  getCenteredOffsetX,
  getEdgeMargin,
  getOpenPanelRect,
  projectBubbleOntoPerimeter,
} from '@/utils/chatbotBubblePosition';

const EXTERNAL_LINK_ICON_URL = require('@/assets/icons/nds/external-link.svg');

const DRAG_CLICK_THRESHOLD_PX = 6;
const CHATBOT_QUERY_PARAM = 'chatbot';
const CHATBOT_FULLSCREEN_QUERY_VALUE = 'fullscreen';

export default {
  name: 'ChatbotElement',
  components: {
    ButtonElement,
    GenericPopup,
    ProgressCircularElement,
    SvgElement,
    ToggleElement,
  },
  setup() {
    return {
      intercom: useIntercom(),
    };
  },
  data() {
    return {
      draft_message: '',
      history_search_query: '',
      show_delete_conversation_popup: false,
      conversation_id_pending_delete: null,
      is_dragging: false,
      drag_start_client_x: 0,
      drag_start_client_y: 0,
      drag_start_offset_x: 0,
      drag_start_offset_y: 0,
      drag_start_edge: 'bottom',
      drag_grab_offset_x: 0,
      drag_grab_offset_y: 0,
      drag_uses_default_position: false,
      suppress_bubble_click: false,
      show_settings: false,
      is_expanded: false,
      applied_fullscreen_query_path: null,
      is_mobile_viewport: false,
      viewport_width: typeof window === 'undefined' ? 0 : window.innerWidth,
      viewport_height: typeof window === 'undefined' ? 0 : window.innerHeight,
      expanded_citation_key: null,
      font_family_options: CHATBOT_FONT_FAMILY_OPTIONS,
      font_size_options: CHATBOT_FONT_SIZE_OPTIONS,
      draft_api_key: '',
      show_api_key: false,
      is_saving_api_key: false,
    };
  },
  computed: {
    ...mapGetters({
      is_connected: 'user/isConnected',
      user_token: 'user/token',
      user_data: 'user/userData',
      user_property_list: 'user/userPropertyList',
      user_actual_experience: 'user/userActualExperience',
      viewed_contact: 'contact/contact',
      viewed_account: 'account/account',
      is_open: 'chatbot/isOpen',
      show_conversations: 'chatbot/showConversations',
      messages: 'chatbot/messages',
      conversation_id: 'chatbot/conversationId',
      conversations: 'chatbot/conversations',
      site: 'chatbot/site',
      parameters: 'chatbot/parameters',
      is_loading: 'chatbot/isLoading',
      is_loading_conversations: 'chatbot/isLoadingConversations',
      is_streaming: 'chatbot/isStreaming',
      stream_status: 'chatbot/streamStatus',
      intercom_handoff_query: 'chatbot/intercomHandoffQuery',
      show_workflow_steps: 'chatbot/showWorkflowSteps',
      has_messages: 'chatbot/hasMessages',
      chatbot_settings: 'chatbot/settings',
      drag_offset_x: 'chatbot/dragOffsetX',
      drag_offset_y: 'chatbot/dragOffsetY',
      drag_edge: 'chatbot/dragEdge',
    }),
    should_show_chatbot() {
      return this.is_connected && this.user_data?.can_use_ai_assistant;
    },
    is_chatbot_admin() {
      const admin_level_id = Number(this.user_data?.admin_level?.id);

      return Number.isFinite(admin_level_id) && admin_level_id > 0;
    },
    enter_sends_message() {
      return this.chatbot_settings?.enter_sends_message !== false;
    },
    should_render_markdown() {
      if (!this.is_chatbot_admin) {
        return true;
      }

      return this.chatbot_settings?.render_markdown !== false;
    },
    should_show_loading_steps() {
      if (this.is_chatbot_admin) {
        return this.chatbot_settings?.show_detailed_loading_steps !== false;
      }

      return this.show_workflow_steps;
    },
    display_stream_status() {
      if (!this.stream_status) {
        return '';
      }

      if (this.is_chatbot_admin && this.chatbot_settings?.raw_workflow_steps) {
        return this.stream_status;
      }

      return parseWorkflowNodeStatusLabel(this.stream_status);
    },
    chatbot_content_style() {
      return {
        '--chatbot-font-family': getChatbotFontFamilyCss(
          this.chatbot_settings?.font_family
        ),
        '--chatbot-font-size': `${this.chatbot_settings?.font_size || 15}px`,
        '--chatbot-line-height': `${Math.round((this.chatbot_settings?.font_size || 15) * 1.35)}px`,
        '--chatbot-external-link-icon': `url("${EXTERNAL_LINK_ICON_URL}")`,
      };
    },
    layout_metrics() {
      const viewport_width = this.viewport_width || (
        typeof window === 'undefined' ? 0 : window.innerWidth
      );
      const viewport_height = this.viewport_height || (
        typeof window === 'undefined' ? 0 : window.innerHeight
      );

      return {
        viewport_width,
        viewport_height,
        margin: getEdgeMargin(viewport_width),
        header_height: CHATBOT_HEADER_HEIGHT_PX,
        bubble_size: CHATBOT_BUBBLE_SIZE_PX,
        panel_width: CHATBOT_PANEL_WIDTH_PX,
        panel_gap: CHATBOT_PANEL_GAP_PX,
      };
    },
    bubble_bounds() {
      const metrics = this.layout_metrics;

      return getBubbleBounds({
        viewport_width: metrics.viewport_width,
        viewport_height: metrics.viewport_height,
        margin: metrics.margin,
        header_height: metrics.header_height,
        bubble_size: metrics.bubble_size,
      });
    },
    resolved_drag_position() {
      const bounds = this.bubble_bounds;
      const metrics = this.layout_metrics;

      if (this.drag_offset_x === null || this.drag_offset_x === undefined) {
        return clampBubblePosition({
          edge: 'bottom',
          offset_x: getCenteredOffsetX({
            viewport_width: metrics.viewport_width,
            margin: metrics.margin,
            bubble_size: metrics.bubble_size,
          }),
          offset_y: 0,
          bounds,
        });
      }

      return clampBubblePosition({
        edge: this.drag_edge || 'bottom',
        offset_x: this.drag_offset_x,
        offset_y: this.drag_offset_y || 0,
        bounds,
      });
    },
    chatbot_position_style() {
      const position = this.resolved_drag_position;

      return {
        right: `${this.layout_metrics.margin}px`,
        bottom: `${this.layout_metrics.margin}px`,
        transform: `translate(${position.offset_x}px, ${position.offset_y}px)`,
      };
    },
    chatbot_panel_style() {
      const style = {
        ...this.chatbot_content_style,
      };

      if (this.is_expanded || !this.is_open) {
        return style;
      }

      const position = this.resolved_drag_position;
      const metrics = this.layout_metrics;
      const bounds = this.bubble_bounds;
      const panel = getOpenPanelRect({
        edge: position.edge,
        offset_x: position.offset_x,
        offset_y: position.offset_y,
        bounds,
        viewport_width: metrics.viewport_width,
        viewport_height: metrics.viewport_height,
        margin: metrics.margin,
        header_height: metrics.header_height,
        bubble_size: metrics.bubble_size,
        panel_max_width: metrics.panel_width,
        panel_gap: metrics.panel_gap,
        reserve_corner_lanes: this.is_dragging && this.suppress_bubble_click,
      });
      const bubble_left = bounds.right + position.offset_x;
      const bubble_top = bounds.bottom + position.offset_y;

      return {
        ...style,
        top: `${panel.top - bubble_top}px`,
        left: `${panel.left - bubble_left}px`,
        right: 'auto',
        bottom: 'auto',
        width: `${panel.width}px`,
        height: `${panel.height}px`,
        maxWidth: `${panel.width}px`,
        maxHeight: `${panel.height}px`,
      };
    },
    show_history_panel() {
      if (this.show_conversations) {
        return true;
      }

      return this.is_expanded && !this.is_mobile_viewport;
    },
    show_history_toggle() {
      return !this.is_expanded || this.is_mobile_viewport;
    },
    is_full_layout() {
      return this.is_expanded && !this.is_mobile_viewport;
    },
    pending_delete_conversation_name() {
      const conversation = this.conversations.find(
        (item) => item.id === this.conversation_id_pending_delete
      );

      if (!conversation) {
        return this.$t('untitled_conversation');
      }

      return this.conversationName(conversation);
    },
    delete_conversation_warning() {
      return this.$t('delete_conversation_warning', {
        name: this.pending_delete_conversation_name,
      });
    },
    filtered_conversations() {
      const query = this.history_search_query.trim().toLowerCase();

      if (!query) {
        return this.conversations;
      }

      return this.conversations.filter((conversation) => {
        const name = this.conversationName(conversation).toLowerCase();

        return name.includes(query);
      });
    },
    chatbot_title() {
      return this.site?.title || this.$t('title');
    },
    current_conversation_title() {
      const conversation = this.conversations.find(
        (item) => item.id === this.conversation_id
      );
      const name = (conversation?.name || '').trim();

      if (!name) {
        return this.$t('provisional_conversation_title');
      }

      return name;
    },
    stable_user_id() {
      if (!this.user_data) {
        return undefined;
      }

      return `${this.user_data.id}-${this.user_data.first_name}-${this.user_data.last_name}`
        .toLowerCase();
    },
    opening_statement() {
      return pickOpeningStatement(
        this.parameters?.opening_statement,
        this.$route,
        this.suggestion_template_variables
      );
    },
    page_context() {
      return buildPageContext(this.$route);
    },
    account_id() {
      return getAccountIdFromRoute(this.$route);
    },
    suggested_questions() {
      return (this.parameters?.suggested_questions || [])
        .filter((question) => typeof question === 'string' && question.trim())
        .map((question) => parseSuggestedQuestion(question))
        .filter((parsed) => parsed.label && suggestionMatchesRoute(parsed, this.$route))
        .map((parsed) => {
          const templated = applySuggestionTemplate(
            parsed.label,
            this.suggestion_template_variables
          );

          return {
            label: templated.label,
            is_complete: templated.is_complete,
            is_contextual: !parsed.is_wildcard,
          };
        })
        .filter((question) => question.is_complete && question.label);
    },
    show_welcome() {
      return !this.has_messages;
    },
    can_send() {
      return Boolean(this.draft_message.trim())
        && !this.is_streaming
        && !this.is_current_conversation_read_only
        && Boolean(this.stable_user_id);
    },
    is_current_conversation_read_only() {
      const conversation = this.conversations.find(
        (item) => item.id === this.conversation_id
      );

      return this.isConversationReadOnly(conversation);
    },
    composer_placeholder() {
      if (this.is_current_conversation_read_only) {
        return this.$t('placeholder_read_only');
      }

      return this.$t('placeholder');
    },
    composer_aria_label() {
      if (this.is_current_conversation_read_only) {
        return this.$t('placeholder_read_only');
      }

      return undefined;
    },
    chatbot_environment() {
      return configuration.chatbot_environment;
    },
    desk_v1_host() {
      return configuration.desk_v1_host || '';
    },
    value_proposition() {
      return this.user_property_list?.find(
        (property) => property.name === 'value_proposition'
      )?.value || '';
    },
    current_job() {
      return this.user_actual_experience?.job_title || '';
    },
    current_company() {
      return this.user_actual_experience?.company?.name || '';
    },
    organization_id() {
      return this.user_data?.organization?.id ?? 0;
    },
    contact_in_context() {
      // Page stores keep the last visited record, so ignore it once we leave its page.
      const contact_id = Number(this.$route?.params?.contact_id);

      if (!Number.isFinite(contact_id) || Number(this.viewed_contact?.id) !== contact_id) {
        return null;
      }

      return this.viewed_contact;
    },
    account_in_context() {
      if (!this.account_id || Number(this.viewed_account?.id) !== this.account_id) {
        return null;
      }

      return this.viewed_account;
    },
    // add new template variables for the chatbot suggestions here
    suggestion_template_variables() {
      return {
        contact_first_name: this.contact_in_context?.first_name || '',
        contact_last_name: this.contact_in_context?.last_name || '',
        account_name: this.account_in_context?.name || '',
      };
    },
    saved_api_key() {
      return this.chatbot_settings?.api_key || '';
    },
    can_save_api_key() {
      if (this.is_saving_api_key) {
        return false;
      }

      return this.draft_api_key.trim() !== this.saved_api_key;
    },
  },
  watch: {
    should_show_chatbot: {
      immediate: true,
      handler(should_show) {
        if (!should_show) {
          this.applied_fullscreen_query_path = null;
          this.resetOnLogout();
          this.resetDragPosition();
          this.show_settings = false;
          this.is_expanded = false;
          this.clearHistorySearch();
          this.unlockBodyScroll();

          return;
        }

        this.openFullscreenFromQuery();
      },
    },
    '$route.fullPath'() {
      this.openFullscreenFromQuery();
    },
    is_open(is_open) {
      if (is_open && this.should_show_chatbot) {
        this.bootstrapPanel();
        this.focusComposerInput();
        this.resizeComposer();
        this.lockBodyScroll();
      }

      if (!is_open) {
        this.show_settings = false;
        this.is_expanded = false;
        this.clearHistorySearch();
        this.unlockBodyScroll();
      }
    },
    show_settings(show_settings) {
      if (show_settings) {
        this.syncDraftApiKey();
      } else {
        this.show_api_key = false;
      }
    },
    is_expanded(is_expanded) {
      if (is_expanded) {
        this.lockBodyScroll();
        this.$nextTick(() => {
          this.scrollMessagesToBottom();
          this.focusComposerInput();
        });
      } else if (!this.is_open) {
        this.unlockBodyScroll();
      }

      this.resizeComposer();
      this.fitMarkdownTables();
    },
    draft_message() {
      this.resizeComposer();
    },
    is_current_conversation_read_only(is_read_only) {
      if (!is_read_only) {
        return;
      }

      this.draft_message = '';
      this.blurComposerInput();
    },
    'chatbot_settings.font_size'() {
      this.resizeComposer();
      this.fitMarkdownTables();
    },
    intercom_handoff_query(query) {
      if (!query) {
        return;
      }

      this.openIntercomDraft(query);
      this.clearIntercomHandoff();
    },
    messages: {
      deep: true,
      handler() {
        this.$nextTick(() => {
          fitChatbotTableColumns(this.$refs.messages_container);

          if (this.chatbot_settings?.auto_scroll !== false) {
            this.scrollMessagesToBottom();
          }
        });
      },
    },
  },
  mounted() {
    this.viewport_width = window.innerWidth;
    this.viewport_height = window.innerHeight;
    this.is_mobile_viewport = window.innerWidth <= 640;
    this.syncClampedDragOffset({ persist: true });
    window.addEventListener('resize', this.onWindowResize);
    window.addEventListener('keydown', this.onWindowKeydown);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize);
    window.removeEventListener('keydown', this.onWindowKeydown);
    this.removeDragListeners();
    this.unlockBodyScroll();

    if (!this.should_show_chatbot) {
      this.resetOnLogout();
    }
  },
  methods: {
    ...mapActions({
      triggerToast: 'toast_v2/triggerToast',
      getUserPropertyList: 'user/getUserPropertyList',
      getUserExperienceList: 'user/getUserExperienceList',
      toggleChatbot: 'chatbot/toggleChatbot',
      openConversationsMenu: 'chatbot/openConversationsMenu',
      closeConversationsMenu: 'chatbot/closeConversationsMenu',
      fetchSiteSettings: 'chatbot/fetchSiteSettings',
      fetchParameters: 'chatbot/fetchParameters',
      fetchConversations: 'chatbot/fetchConversations',
      loadConversation: 'chatbot/loadConversation',
      deleteConversation: 'chatbot/deleteConversation',
      startNewConversation: 'chatbot/startNewConversation',
      sendMessage: 'chatbot/sendMessage',
      stopGeneration: 'chatbot/stopGeneration',
      clearIntercomHandoff: 'chatbot/clearIntercomHandoff',
      resetOnLogout: 'chatbot/resetOnLogout',
      updateSettings: 'chatbot/updateSettings',
      applyApiKeyChange: 'chatbot/applyApiKeyChange',
      updateDragOffset: 'chatbot/updateDragOffset',
    }),
    showErrorToast(translation_key) {
      this.triggerToast({
        style: 'error',
        text: this.$t(translation_key),
        timeout: 5000,
      });
    },
    renderMarkdown,
    onToggleSettings() {
      this.show_settings = !this.show_settings;
    },
    settingsClickOutsideInclude() {
      const settings_toggle = this.$refs.settings_toggle;

      if (!settings_toggle) {
        return [];
      }

      return [settings_toggle];
    },
    onCloseSettings() {
      this.show_settings = false;
    },
    onClickOutsideSettings() {
      if (this.is_expanded) {
        return;
      }

      this.onCloseSettings();
    },
    lockBodyScroll() {
      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      document.body.style.overflow = '';
    },
    onWindowKeydown(event) {
      if (event.key === 'Escape' && this.is_expanded) {
        this.onCollapse();
      }
    },
    openFullscreenFromQuery() {
      if (!this.should_show_chatbot) {
        return;
      }

      const full_path = this.$route?.fullPath;
      const query_value = this.$route?.query?.[CHATBOT_QUERY_PARAM];

      if (query_value !== CHATBOT_FULLSCREEN_QUERY_VALUE) {
        this.applied_fullscreen_query_path = null;

        return;
      }

      if (!full_path || this.applied_fullscreen_query_path === full_path) {
        return;
      }

      this.applied_fullscreen_query_path = full_path;

      if (this.is_expanded) {
        return;
      }

      this.onExpand();
    },
    async onToggleExpand() {
      if (this.is_expanded) {
        this.onCollapse();

        return;
      }

      await this.onExpand();
    },
    async onExpand() {
      if (!this.is_open) {
        await this.toggleChatbot();
      }

      this.show_settings = false;
      this.closeConversationsMenu();

      if (this.stable_user_id) {
        try {
          await this.fetchConversations({
            user: this.stable_user_id,
            environment: this.chatbot_environment,
          });
        } catch {
          this.showErrorToast('error_load_conversations');
        }
      }

      this.is_expanded = true;
    },
    onCollapse() {
      this.is_expanded = false;
      this.show_settings = false;
      this.focusComposerInput();
    },
    onScrimClick() {
      if (this.is_expanded) {
        this.onCollapse();

        return;
      }

      this.onCloseChatbot();
    },
    onCloseChatbot() {
      if (!this.is_open) {
        return;
      }

      this.toggleChatbot();
    },
    onFontFamilyChange(event) {
      this.updateSettings({ font_family: event.target.value });
    },
    onFontSizeChange(font_size) {
      this.updateSettings({ font_size });
    },
    onAutoScrollChange(auto_scroll) {
      this.updateSettings({ auto_scroll });
    },
    onEnterSendsMessageChange(enter_sends_message) {
      this.updateSettings({ enter_sends_message });
    },
    onRenderMarkdownChange(render_markdown) {
      this.updateSettings({ render_markdown });
    },
    onDetailedLoadingStepsChange(show_detailed_loading_steps) {
      this.updateSettings({ show_detailed_loading_steps });
    },
    onFormattedWorkflowStepsChange(formatted) {
      if (this.chatbot_settings?.show_detailed_loading_steps === false) {
        return;
      }

      this.updateSettings({ raw_workflow_steps: !formatted });
    },
    onFollowUpSuggestionsChange(show_follow_up_suggestions) {
      this.updateSettings({ show_follow_up_suggestions });
    },
    onSourcesChange(show_sources) {
      this.updateSettings({ show_sources });
    },
    syncDraftApiKey() {
      this.draft_api_key = this.saved_api_key;
      this.show_api_key = false;
    },
    onToggleApiKeyVisibility() {
      this.show_api_key = !this.show_api_key;
    },
    async onSaveApiKey() {
      if (!this.can_save_api_key) {
        return;
      }

      this.is_saving_api_key = true;

      try {
        await this.applyApiKeyChange({ api_key: this.draft_api_key });
        this.syncDraftApiKey();
        await this.bootstrapPanel();
      } finally {
        this.is_saving_api_key = false;
      }
    },
    syncClampedDragOffset({ persist = false } = {}) {
      if (this.drag_offset_x === null || this.drag_offset_x === undefined) {
        return;
      }

      const next = clampBubblePosition({
        edge: this.drag_edge || 'bottom',
        offset_x: this.drag_offset_x,
        offset_y: this.drag_offset_y || 0,
        bounds: this.bubble_bounds,
      });
      const same_position = next.edge === (this.drag_edge || 'bottom')
        && next.offset_x === this.drag_offset_x
        && next.offset_y === (this.drag_offset_y || 0);

      if (same_position) {
        return;
      }

      this.updateDragOffset({
        offset_x: next.offset_x,
        offset_y: next.offset_y,
        edge: next.edge,
        persist,
        position_set: true,
      });
    },
    onWindowResize() {
      this.viewport_width = window.innerWidth;
      this.viewport_height = window.innerHeight;
      this.is_mobile_viewport = window.innerWidth <= 640;
      this.syncClampedDragOffset({ persist: true });
      this.fitMarkdownTables();
    },
    resetDragPosition() {
      this.is_dragging = false;
      this.suppress_bubble_click = false;
      this.removeDragListeners();
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onBubblePointerMove);
      window.removeEventListener('pointerup', this.onBubblePointerUp);
      window.removeEventListener('pointercancel', this.onBubblePointerUp);
    },
    onBubblePointerDown(event) {
      if (event.button !== undefined && event.button !== 0) {
        return;
      }

      const position = this.resolved_drag_position;
      const bounds = this.bubble_bounds;
      const bubble_left = bounds.right + position.offset_x;
      const bubble_top = bounds.bottom + position.offset_y;

      this.is_dragging = true;
      this.suppress_bubble_click = false;
      this.drag_uses_default_position = this.drag_offset_x === null
        || this.drag_offset_x === undefined;
      this.drag_start_client_x = event.clientX;
      this.drag_start_client_y = event.clientY;
      this.drag_start_offset_x = position.offset_x;
      this.drag_start_offset_y = position.offset_y;
      this.drag_start_edge = position.edge;
      this.drag_grab_offset_x = event.clientX - (
        bubble_left + (CHATBOT_BUBBLE_SIZE_PX / 2)
      );
      this.drag_grab_offset_y = event.clientY - (
        bubble_top + (CHATBOT_BUBBLE_SIZE_PX / 2)
      );

      window.addEventListener('pointermove', this.onBubblePointerMove);
      window.addEventListener('pointerup', this.onBubblePointerUp);
      window.addEventListener('pointercancel', this.onBubblePointerUp);
    },
    onBubblePointerMove(event) {
      if (!this.is_dragging) {
        return;
      }

      const delta_x = event.clientX - this.drag_start_client_x;
      const delta_y = event.clientY - this.drag_start_client_y;

      if (Math.hypot(delta_x, delta_y) > DRAG_CLICK_THRESHOLD_PX) {
        this.suppress_bubble_click = true;
      }

      const next = projectBubbleOntoPerimeter({
        center_x: event.clientX - this.drag_grab_offset_x,
        center_y: event.clientY - this.drag_grab_offset_y,
        current_edge: this.drag_edge || this.drag_start_edge,
        bounds: this.bubble_bounds,
        bubble_size: CHATBOT_BUBBLE_SIZE_PX,
      });

      this.updateDragOffset({
        offset_x: next.offset_x,
        offset_y: next.offset_y,
        edge: next.edge,
        persist: false,
        position_set: true,
      });
    },
    onBubblePointerUp() {
      const did_drag = this.suppress_bubble_click;

      this.is_dragging = false;
      this.removeDragListeners();

      if (!did_drag) {
        if (this.drag_uses_default_position) {
          this.updateDragOffset({
            offset_x: null,
            offset_y: null,
            edge: 'bottom',
            persist: false,
            position_set: false,
          });
        } else {
          this.updateDragOffset({
            offset_x: this.drag_start_offset_x,
            offset_y: this.drag_start_offset_y,
            edge: this.drag_start_edge,
            persist: false,
            position_set: true,
          });
        }

        return;
      }

      this.updateDragOffset({
        offset_x: this.drag_offset_x,
        offset_y: this.drag_offset_y,
        edge: this.drag_edge || 'bottom',
        persist: true,
        position_set: true,
      });
    },
    onBubbleClick() {
      if (this.suppress_bubble_click) {
        this.suppress_bubble_click = false;

        return;
      }

      this.toggleChatbot();
    },
    isConversationReadOnly(conversation) {
      return conversation?.read_only === true;
    },
    conversationHistoryLabel(conversation) {
      if (!this.isConversationReadOnly(conversation)) {
        return undefined;
      }

      const name = this.conversationName(conversation);

      return `${name} (${this.$t('read_only_conversation')})`;
    },
    conversationName(conversation) {
      const name = (conversation?.name || '').trim();

      if (!name) {
        return this.$t('provisional_conversation_title');
      }

      return name;
    },
    formatConversationDate(conversation) {
      const timestamp = conversation?.updated_at || conversation?.created_at;

      if (!timestamp) {
        return '';
      }

      const date = new Date(
        typeof timestamp === 'number' ? timestamp * 1000 : timestamp
      );

      if (Number.isNaN(date.getTime())) {
        return '';
      }

      return date.toLocaleString(this.$i18n.locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatMessageTime(created_at) {
      if (!created_at) {
        return '';
      }

      const date = new Date(created_at);

      if (Number.isNaN(date.getTime())) {
        return '';
      }

      const now = new Date();
      const is_today = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();

      if (is_today) {
        return date.toLocaleTimeString(this.$i18n.locale, {
          hour: '2-digit',
          minute: '2-digit',
        });
      }

      const options = {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      };

      if (date.getFullYear() !== now.getFullYear()) {
        options.year = 'numeric';
      }

      return date.toLocaleString(this.$i18n.locale, options);
    },
    async bootstrapPanel() {
      if (!this.stable_user_id) {
        return;
      }

      try {
        await this.fetchSiteSettings();
      } catch {
        // Keep the i18n title if /site is unavailable
      }

      try {
        await this.fetchParameters({ user: this.stable_user_id });
      } catch {
        this.showErrorToast('error_load_parameters');
      }

      try {
        await this.getUserPropertyList({
          name_list: ['value_proposition'],
        });
      } catch {
        // Keep empty value_proposition if user properties are unavailable
      }

      try {
        await this.getUserExperienceList();
      } catch {
        // Keep empty current_job / current_company if experience is unavailable
      }

      try {
        await this.fetchConversations({
          user: this.stable_user_id,
          environment: this.chatbot_environment,
        });
      } catch {
        this.showErrorToast('error_load_conversations');
      }

      this.$nextTick(() => {
        this.scrollMessagesToBottom();
        fitChatbotTableColumns(this.$refs.messages_container);
      });
    },
    scrollMessagesToBottom() {
      const container = this.$refs.messages_container;

      if (!container) {
        return;
      }

      container.scrollTop = container.scrollHeight;
    },
    focusComposerInput() {
      this.$nextTick(() => {
        const composer_input = this.$refs.composer_input;

        if (
          !composer_input
          || composer_input.disabled
          || this.is_current_conversation_read_only
        ) {
          return;
        }

        composer_input.focus();
      });
    },
    blurComposerInput() {
      this.$nextTick(() => {
        const composer_input = this.$refs.composer_input;

        if (!composer_input) {
          return;
        }

        composer_input.blur();
      });
    },
    fitMarkdownTables() {
      this.$nextTick(() => {
        fitChatbotTableColumns(this.$refs.messages_container);
      });
    },
    resizeComposer() {
      this.$nextTick(() => {
        const composer_input = this.$refs.composer_input;

        if (!composer_input) {
          return;
        }

        const style = window.getComputedStyle(composer_input);
        const border_y = (parseFloat(style.borderTopWidth) || 0)
          + (parseFloat(style.borderBottomWidth) || 0);

        composer_input.style.height = '0px';
        composer_input.style.height = `${composer_input.scrollHeight + border_y}px`;
      });
    },
    clearHistorySearch() {
      this.history_search_query = '';
    },
    onCloseConversations() {
      this.clearHistorySearch();
      this.closeConversationsMenu();
      this.focusComposerInput();
    },
    async onToggleConversations() {
      if (this.show_conversations) {
        this.clearHistorySearch();
        this.closeConversationsMenu();
        this.focusComposerInput();

        return;
      }

      if (!this.stable_user_id) {
        return;
      }

      this.show_settings = false;

      try {
        await this.openConversationsMenu({
          user: this.stable_user_id,
          environment: this.chatbot_environment,
        });
      } catch {
        this.showErrorToast('error_load_conversations');
      }
    },
    async onNewConversation() {
      this.expanded_citation_key = null;
      await this.startNewConversation();
      this.focusComposerInput();
    },
    async onSelectConversation(conversation_id) {
      if (!this.stable_user_id || conversation_id === this.conversation_id) {
        this.closeConversationsMenu();
        this.focusComposerInput();

        return;
      }

      this.expanded_citation_key = null;
      try {
        await this.loadConversation({
          conversation_id,
          user: this.stable_user_id,
        });
        this.focusComposerInput();
      } catch {
        this.showErrorToast('error_load_history');
      }
    },
    onRequestDeleteConversation(conversation_id) {
      if (!conversation_id || this.is_streaming || this.is_loading) {
        return;
      }

      this.conversation_id_pending_delete = conversation_id;
      this.show_delete_conversation_popup = true;
    },
    onCloseDeleteConversationPopup() {
      this.conversation_id_pending_delete = null;
    },
    async onConfirmDeleteConversation() {
      const conversation_id = this.conversation_id_pending_delete;

      this.show_delete_conversation_popup = false;

      await this.onDeleteConversation(conversation_id);
    },
    async onDeleteConversation(conversation_id) {
      if (!this.stable_user_id || !conversation_id) {
        return;
      }

      try {
        await this.deleteConversation({
          conversation_id,
          user: this.stable_user_id,
        });
      } catch {
        this.showErrorToast('error_delete_conversation');
      }
    },
    onComposerKeydown(event) {
      if (event.key !== 'Enter' || event.isComposing) {
        return;
      }

      const submit_with_enter = this.enter_sends_message
        ? !event.shiftKey
        : event.shiftKey;

      if (!submit_with_enter) {
        return;
      }

      event.preventDefault();
      this.onSend();
    },
    async onSuggestedQuestion(question) {
      if (this.is_current_conversation_read_only) {
        return;
      }

      this.draft_message = question;
      await this.onSend();
    },
    shouldShowFollowUpQuestions(message) {
      if (this.is_current_conversation_read_only) {
        return false;
      }

      if (!this.chatbot_settings?.show_follow_up_suggestions) {
        return false;
      }

      if (
        this.is_streaming
        || message?.role !== 'assistant'
        || message?.status !== 'done'
        || !message?.suggested_questions?.length
      ) {
        return false;
      }

      const last_assistant = [...this.messages]
        .reverse()
        .find((item) => item.role === 'assistant');

      return last_assistant?.id === message.id;
    },
    shouldShowCitations(message) {
      if (!this.chatbot_settings?.show_sources) {
        return false;
      }

      if (message?.role !== 'assistant' || message?.status !== 'done') {
        return false;
      }

      return Boolean(message?.retriever_resources?.length);
    },
    getCitationKey(message_id, resource) {
      return `${message_id}:${resource.id}:${resource.position}`;
    },
    isCitationExpanded(message_id, resource) {
      return this.expanded_citation_key === this.getCitationKey(message_id, resource);
    },
    toggleCitation(message_id, resource) {
      const key = this.getCitationKey(message_id, resource);

      this.expanded_citation_key = this.expanded_citation_key === key
        ? null
        : key;
    },
    formatCitationScore(score) {
      if (!Number.isFinite(score)) {
        return '';
      }

      return `${Math.round(score * 100)}%`;
    },
    async onSend() {
      if (!this.can_send) {
        return;
      }

      const query = this.draft_message.trim();

      this.draft_message = '';
      this.closeConversationsMenu();
      this.show_settings = false;

      try {
        await this.sendMessage({
          query,
          user: this.stable_user_id,
          inputs: {
            user_token: this.user_token,
            page_context: this.page_context,
            account_id: this.account_id,
            environment: this.chatbot_environment,
            value_proposition: this.value_proposition,
            current_job: this.current_job,
            current_company: this.current_company,
            organization_id: this.organization_id,
            desk_v1_host: this.desk_v1_host,
          },
        });
      } catch {
        this.showErrorToast('error_send');
      }
    },
    async onStop() {
      if (!this.stable_user_id) {
        return;
      }

      await this.stopGeneration({ user: this.stable_user_id });
    },
    openIntercomDraft(query) {
      if (typeof window.Intercom !== 'function') {
        return;
      }

      this.intercom.showNewMessage(query);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/constants/variables.module.scss';

.chatbot-scrim {
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: $app-header-height;
  z-index: 1100;

  &--expanded {
    backdrop-filter: blur(8px);
    background-color: rgba($haiti, 0.12);
  }
}

.chatbot-element {
  bottom: 24px;
  position: fixed;
  right: 24px;
  z-index: 1200;
  will-change: transform;

  &--expanded {
    bottom: 0;
    left: 0;
    pointer-events: none;
    right: 0;
    top: $app-header-height;
    will-change: auto;
  }
}

.chatbot-bubble {
  align-items: center;
  background-color: $chatbot-bubble-background;
  border: 1px solid $chatbot-bubble-border;
  border-radius: 20px;
  box-shadow: 6.22px 6.22px 15.56px 0px rgba(0, 0, 0, 0.15);
  box-sizing: border-box;
  color: $chatbot-purple;
  cursor: grab;
  display: flex;
  flex: 0 0 56px;
  height: 56px;
  justify-content: center;
  margin: 0;
  max-height: 56px;
  max-width: 56px;
  min-height: 56px;
  min-width: 56px;
  padding: 0;
  position: relative;
  touch-action: none;
  user-select: none;
  width: 56px;
  z-index: 1;

  :deep(svg) {
    --color: #{$chatbot-purple};
    --color-eye: #{$white};
    --color-face: #000000;
    display: block;
  }

  &:hover {
    background-color: darken($chatbot-bubble-background, 4%);
  }

  &--dragging {
    cursor: grabbing;
  }
}

.chatbot-panel {
  background-color: $white-pointer;
  border: 1px solid $greyscale-semi-light;
  border-radius: 16px;
  bottom: 72px;
  box-shadow: 0 2px 4px rgba($haiti, 0.12), 0 8px 16px rgba($haiti, 0.18);
  display: flex;
  flex-direction: column;
  font-family: var(--chatbot-font-family, 'DM Sans', sans-serif);
  font-size: var(--chatbot-font-size, 15px);
  height: calc(100vh - #{$app-header-height} - 24px - 72px - 24px);
  line-height: var(--chatbot-line-height, 20px);
  max-width: calc(100vw - 48px);
  overflow: hidden;
  pointer-events: auto;
  position: absolute;
  right: 0;
  width: 520px;

  .nds-text-p,
  .nds-text-p-bold,
  .nds-text-p-small,
  .nds-text-p-small-bold,
  .nds-text-button-small-bold,
  .nds-text-tag {
    font-family: inherit;
    font-size: inherit;
    letter-spacing: normal;
    line-height: inherit;
  }

  &--expanded {
    bottom: 24px;
    height: auto;
    left: 24px;
    max-width: none;
    right: 24px;
    top: 24px;
    transform: none;
    width: auto;
  }
}

.chatbot-panel-header {
  align-items: center;
  border-bottom: 1px solid $greyscale-semi-light;
  display: flex;
  justify-content: space-between;
  padding: 12px 16px;

  &--expanded {
    align-items: stretch;
    box-sizing: border-box;
    height: 68px;
    justify-content: flex-start;
    padding: 0;
  }
}

.chatbot-panel-header-brand {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 8px;
  min-width: 0;
}

.chatbot-panel-header--expanded .chatbot-panel-header-brand {
  background-color: $white;
  border-right: 1px solid $greyscale-semi-light;
  box-sizing: border-box;
  flex: 0 0 280px;
  padding: 12px 12px 12px 16px;
  width: 280px;
}

.chatbot-panel-header .chatbot-brand-icon {
  --color: #{$chatbot-purple};
  --color-eye: #{$white};
  --color-face: #000000;
  display: block;
  flex-shrink: 0;
}

.chatbot-panel-header-conversation {
  align-items: center;
  display: flex;
  flex: 1;
  min-width: 0;
  padding: 12px 8px 12px 16px;
}

.chatbot-conversation-title.nds-text-p-bold {
  color: $haiti;
  font-size: 16px;
  line-height: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-panel-header-title.nds-text-p-bold {
  color: $haiti;
  flex: 1;
  font-size: 16px;
  line-height: 20px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-panel-header--expanded .chatbot-panel-header-actions {
  padding-right: 12px;
}

.chatbot-panel-header-actions {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 4px;
}

.chatbot-icon-button {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  width: 32px;

  :deep(svg) {
    --color: #{$haiti};
  }

  &:hover:not(:disabled) {
    background-color: $low-grey;

    :deep(svg) {
      --color: #{$chatbot-purple};
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &--active {
    background-color: $low-grey;

    :deep(svg) {
      --color: #{$chatbot-purple};
    }
  }
}

.chatbot-panel-body {
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;
}

.chatbot-history-menu {
  background-color: $white;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  left: 0;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 2;

  &--sidebar {
    border-right: 1px solid $greyscale-semi-light;
    bottom: auto;
    box-sizing: border-box;
    flex-shrink: 0;
    left: auto;
    position: relative;
    right: auto;
    top: auto;
    width: 280px;
    z-index: 1;
  }
}

.chatbot-history-menu-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 0 4px 4px;
}

.chatbot-new-discussion {
  align-items: center;
  background-color: $chatbot-purple;
  border: none;
  border-radius: 8px;
  color: $white;
  cursor: pointer;
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 12px 0;
  min-height: 40px;
  padding: 8px 12px;
  width: auto;

  :deep(svg) {
    --color: #{$white};
  }

  &:hover:not(:disabled) {
    background-color: $chatbot-purple-hover;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.chatbot-history-search {
  align-items: center;
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  display: flex;
  gap: 8px;
  margin: 8px 0;
  padding: 0 8px;
  position: relative;

  &:focus-within {
    border-color: $chatbot-purple;
  }

  :deep(svg) {
    --color: #{$greyscale-medium};
  }
}

.chatbot-history-search-icon {
  display: flex;
  flex-shrink: 0;
}

#app input.chatbot-history-search-input,
.chatbot-element input.chatbot-history-search-input {
  background: transparent;
  border: none;
  color: $haiti;
  flex: 1;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: normal;
  line-height: inherit;
  min-width: 0;
  outline: none;
  padding: 8px 0;
  width: 100%;

  &::placeholder {
    color: $greyscale-medium;
  }

  &::-webkit-search-cancel-button {
    appearance: none;
    display: none;
  }
}

.chatbot-history-search-clear {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 22px;
  justify-content: center;
  padding: 0;
  width: 22px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover {
    background-color: $low-grey;

    :deep(svg) {
      --color: #{$haiti};
    }
  }
}

.chatbot-history-empty {
  color: $greyscale-medium;
  margin: 12px 4px 0;
}

.chatbot-history-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  margin: 0;
  overflow-y: auto;
  padding: 0 12px 0 0;
  scrollbar-gutter: stable;
}

.chatbot-history-row {
  position: relative;

  &:hover .chatbot-history-delete:not(:disabled),
  &:focus-within .chatbot-history-delete:not(:disabled) {
    opacity: 1;
  }

  &:hover .chatbot-history-item:not(:disabled):not(.chatbot-history-item--active),
  &:focus-within .chatbot-history-item:not(:disabled):not(.chatbot-history-item--active) {
    background-color: $low-grey;
  }
}

.chatbot-history-item {
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &--active {
    background-color: $chatbot-purple-secondary;
  }
}

.chatbot-history-item-heading {
  align-items: flex-start;
  display: flex;
  gap: 6px;
  min-width: 0;
}

.chatbot-history-readonly {
  align-items: center;
  display: flex;
  flex-shrink: 0;
  margin-top: 1px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }
}

.chatbot-history-delete {
  align-items: center;
  background-color: $low-grey;
  border: none;
  border-radius: 6px;
  color: $haiti;
  cursor: pointer;
  display: flex;
  height: 28px;
  justify-content: center;
  opacity: 0;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.12s ease;
  width: 28px;
  z-index: 1;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover:not(:disabled) {
    :deep(svg) {
      --color: #{$status-error};
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0;
  }
}

.chatbot-history-item-name {
  color: $haiti;
  display: -webkit-box;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.chatbot-history-item-date {
  color: $greyscale-medium;
}

.chatbot-main-area {
  display: flex;
  flex: 1;
  min-height: 0;
  min-width: 0;
  position: relative;
}

.chatbot-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}

.chatbot-panel--expanded .chatbot-messages,
.chatbot-panel--expanded .chatbot-composer {
  margin: 0;
  max-width: none;
  width: 100%;
}

.chatbot-panel--expanded .chatbot-messages {
  padding: 24px 32px;
}

.chatbot-panel--expanded .chatbot-composer {
  padding: 12px 32px 20px;
}

.chatbot-messages {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 16px;
}

.chatbot-loading {
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  padding: 24px;
}

.chatbot-message-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 8px;
  max-width: 100%;
  min-width: 0;

  .chatbot-message-bubble {
    max-width: 100%;
  }
}

.chatbot-suggestions {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  width: 100%;
}

.chatbot-panel--expanded .chatbot-suggestions {
  align-items: flex-start;
}

.chatbot-citations {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
  max-width: 100%;
}

.chatbot-citations-title {
  color: $greyscale-medium;
  text-transform: uppercase;
}

.chatbot-citations-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chatbot-citation {
  max-width: 100%;
}

.chatbot-citation-toggle {
  align-items: center;
  background: transparent;
  border: 0;
  color: $haiti;
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  max-width: 100%;
  padding: 0;
  text-align: left;

  &:hover .chatbot-citation-name {
    color: $chatbot-purple;
  }
}

.chatbot-citation-index {
  align-items: center;
  background-color: $low-grey;
  border: 1px solid $greyscale-semi-light;
  border-radius: 4px;
  color: $greyscale-medium;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  min-width: 18px;
  padding: 0 4px;
}

.chatbot-citation-name {
  color: $haiti;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chatbot-citation-dataset,
.chatbot-citation-score {
  color: $greyscale-medium;
}

.chatbot-citation-content {
  border-left: 2px solid $greyscale-semi-light;
  color: $greyscale-dark;
  margin: 4px 0 4px 8px;
  max-height: 160px;
  overflow-y: auto;
  padding-left: 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

.chatbot-suggestion {
  background-color: $white;
  border: 1px solid $moody-blue;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  .chatbot-panel--expanded & {
    max-width: 100%;
    width: fit-content;
  }

  &:hover:not(:disabled) {
    border-color: $chatbot-purple;
    color: $chatbot-purple;
  }

  &--contextual {
    border-color: $chatbot-purple;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
}

.chatbot-message {
  align-items: flex-start;
  display: flex;
  gap: 8px;
  width: 100%;

  &--user {
    justify-content: flex-end;

    .chatbot-message-content {
      align-items: flex-end;
      flex: 0 1 auto;
      max-width: 85%;
    }

    .chatbot-message-bubble {
      background-color: $chatbot-purple-secondary;
      color: $haiti;
    }
  }

  &--assistant {
    gap: 14px;
    justify-content: flex-start;

    .chatbot-message-bubble {
      background-color: transparent;
      color: $haiti;
    }
  }
}

.chatbot-message-avatar {
  align-items: center;
  border-radius: 50%;
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  overflow: visible;
  width: 36px;

  &--bot {
    background-color: $white;
    border: 1px solid $chatbot-purple;
    transform: translateX(-6px);

    :deep(svg) {
      --color: #{$chatbot-purple};
      --color-eye: #{$white};
      --color-face: #000000;
    }
  }
}

.chatbot-message-time {
  color: $greyscale-medium;
  margin-top: 4px;
}

#app .chatbot-message-bubble,
.chatbot-element .chatbot-message-bubble {
  border-radius: 12px;
  margin: 0;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px;
  white-space: pre-wrap;
  word-break: break-word;

  :deep(p) {
    margin: 0 0 0.65em;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

#app .chatbot-message--assistant .chatbot-message-bubble,
.chatbot-element .chatbot-message--assistant .chatbot-message-bubble {
  background-color: transparent;
  padding: 5px;
}

#app .chatbot-message--user .chatbot-message-bubble,
.chatbot-element .chatbot-message--user .chatbot-message-bubble {
  background-color: $chatbot-purple-secondary;
  color: $haiti;
}

#app .chatbot-markdown,
.chatbot-element .chatbot-markdown {
  white-space: normal;

  :deep(> *:first-child) {
    margin-top: 0;
  }

  :deep(> *:last-child) {
    margin-bottom: 0;
  }

  :deep(p) {
    margin: 0 0 0.65em;

    &:last-child {
      margin-bottom: 0;
    }
  }

  :deep(ul),
  :deep(ol) {
    margin: 0 0 0.65em;
    padding-left: 1.25em;
  }

  :deep(li) {
    margin: 0.15em 0;
  }

  :deep(strong) {
    font-weight: 700;
  }

  :deep(a) {
    color: inherit;
    text-decoration: underline;
  }

  :deep(a[target="_blank"])::after {
    background-color: currentColor;
    content: '';
    display: inline-block;
    height: 0.85em;
    margin-left: 0.25em;
    -webkit-mask: var(--chatbot-external-link-icon) no-repeat center / contain;
    mask: var(--chatbot-external-link-icon) no-repeat center / contain;
    pointer-events: none;
    vertical-align: -0.1em;
    width: 0.85em;
  }

  :deep(hr) {
    background: transparent;
    border: 0;
    border-top: 1px solid rgba($haiti, 0.18);
    height: 0;
    margin: 0.85em 0;
  }

  :deep(code) {
    background-color: rgba($haiti, 0.08);
    border-radius: 4px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.9em;
    padding: 0.1em 0.35em;
  }

  :deep(pre) {
    background-color: $space-cadet;
    border-radius: 8px;
    color: $white;
    margin: 0 0 0.65em;
    overflow-x: auto;
    padding: 10px 12px;

    code {
      background: transparent;
      color: inherit;
      padding: 0;
    }
  }

  :deep(blockquote) {
    border-left: 3px solid rgba($haiti, 0.25);
    margin: 0 0 0.65em;
    padding-left: 0.75em;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    font-size: 14px;
    font-weight: 700;
    line-height: 18px;
    margin: 0.85em 0 0.45em;
  }

  :deep(table) {
    border-collapse: collapse;
    border-spacing: 0;
    margin: 0;
    width: max-content;
  }

  :deep(.chatbot-md-table) {
    container-type: inline-size;
    margin: 0.5em 0 0.75em;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  :deep(thead) {
    background-color: rgba($haiti, 0.06);
  }

  :deep(th),
  :deep(td) {
    border: 1px solid rgba($haiti, 0.18);
    box-sizing: border-box;
    max-width: calc(50cqw - 1px);
    overflow-wrap: break-word;
    padding: 6px 10px;
    text-align: left;
    vertical-align: top;
    white-space: normal;
  }

  :deep(th:only-child),
  :deep(td:only-child) {
    max-width: calc(100cqw - 1px);
  }

  :deep(th) {
    font-weight: 700;
  }

  :deep(tbody tr:nth-child(even) td) {
    background-color: rgba($haiti, 0.03);
  }
}

.chatbot-message--user .chatbot-message-bubble {
  white-space: pre-wrap;
}

.chatbot-message--assistant .chatbot-markdown {
  :deep(code) {
    background-color: rgba($haiti, 0.06);
  }

  :deep(pre) {
    background-color: $space-cadet;
  }

  :deep(hr) {
    border-top-color: rgba($haiti, 0.16);
  }

  :deep(th),
  :deep(td) {
    border-color: rgba($haiti, 0.16);
  }
}

.chatbot-typing {
  align-items: center;
  display: inline-flex;
  gap: 8px;
  min-height: 18px;
}

.chatbot-typing-label {
  color: inherit;
  opacity: 0.75;
}

.chatbot-typing-dots {
  align-items: center;
  display: inline-flex;
  gap: 4px;
}

.chatbot-typing-dot {
  animation: chatbot-typing-bounce 1.2s ease-in-out infinite;
  background-color: currentColor;
  border-radius: 50%;
  height: 6px;
  opacity: 0.35;
  width: 6px;

  &:nth-child(2) {
    animation-delay: 0.15s;
  }

  &:nth-child(3) {
    animation-delay: 0.3s;
  }
}

@keyframes chatbot-typing-bounce {
  0%,
  80%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }

  40% {
    opacity: 0.9;
    transform: translateY(-3px);
  }
}

.chatbot-composer {
  padding: 8px 16px 12px;
}

.chatbot-composer-field {
  align-items: center;
  display: flex;
  gap: 8px;
}

.chatbot-composer-input-wrap {
  flex: 1;
  min-width: 0;
  position: relative;
}

.chatbot-settings-menu {
  background-color: $white;
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  left: 0;
  overflow-y: auto;
  padding: 12px;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 3;

  &--sidebar {
    align-self: stretch;
    border-left: 1px solid $greyscale-semi-light;
    bottom: auto;
    flex-shrink: 0;
    left: auto;
    position: relative;
    right: auto;
    top: auto;
    width: 360px;
  }
}

.chatbot-settings-menu-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  font-size: 18px;
}

.chatbot-settings-menu-title {
  align-items: center;
  display: flex;
  gap: 8px;

  :deep(svg) {
    --color: #{$haiti};
  }
}

.chatbot-settings-section {
  display: flex;
  flex-direction: column;
  gap: 10px;

  & + & {
    margin-top: 8px;
  }

  :deep(.toggle) {
    align-items: flex-start;
    width: 100%;
  }

  :deep(.toggle-label) {
    font-size: inherit;
    line-height: inherit;
  }
}

.chatbot-settings-section-title {
  color: $haiti;
}

.chatbot-enter-action {
  align-items: center;
  display: flex;
  gap: 8px;
  max-width: 100%;
}

.chatbot-enter-action-label {
  background: transparent;
  border: 0;
  color: $greyscale-medium;
  cursor: pointer;
  flex: 0 0 auto;
  padding: 0;
  text-align: right;
  white-space: nowrap;

  &--end {
    text-align: left;
  }

  &--active {
    color: $haiti;
    font-weight: 700;
  }

  &:focus-visible {
    outline: 2px solid $cornflower-blue;
    outline-offset: 2px;
  }
}

.chatbot-enter-action-switch {
  background-color: $cornflower-blue;
  border: 1px solid $cornflower-blue;
  border-radius: 16px;
  cursor: pointer;
  flex: 0 0 auto;
  height: 16px;
  padding: 0;
  position: relative;
  width: 28px;

  &:hover,
  &:focus-visible {
    background-color: $indigo;
    border-color: $indigo;
  }

  &:focus-visible {
    outline: 2px solid $cornflower-blue;
    outline-offset: 2px;
  }
}

.chatbot-enter-action-knob {
  background-color: $white;
  border: 1px solid $cornflower-blue;
  border-radius: 50%;
  height: 16px;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: -1px;
  transform: translateX(-1px);
  transition: transform 0.3s;
  width: 16px;

  &--end {
    transform: translateX(11px);
  }
}

.chatbot-enter-action-switch:hover .chatbot-enter-action-knob,
.chatbot-enter-action-switch:focus-visible .chatbot-enter-action-knob {
  border-color: $indigo;
}

.chatbot-settings-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.chatbot-settings-field-label {
  align-items: center;
  color: $greyscale-medium;
  display: flex;
  gap: 6px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }
}

.chatbot-settings-select,
.chatbot-settings-input {
  appearance: none;
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  padding: 8px 10px;
  width: 100%;

  &:hover,
  &:focus {
    border-color: $chatbot-purple;
    outline: none;
  }
}

.chatbot-settings-api-key {
  align-items: center;
  display: flex;
  gap: 8px;
}

.chatbot-settings-api-key-field {
  flex: 1;
  min-width: 0;
  position: relative;

  .chatbot-settings-input {
    padding-right: 36px;
  }

  &:hover .chatbot-settings-api-key-reveal,
  &:focus-within .chatbot-settings-api-key-reveal {
    opacity: 1;
    pointer-events: auto;
  }
}

.chatbot-settings-api-key-reveal {
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 6px;
  display: flex;
  height: 28px;
  justify-content: center;
  opacity: 0;
  padding: 0;
  pointer-events: none;
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;

  :deep(svg) {
    --color: #{$greyscale-medium};
  }

  &:hover :deep(svg) {
    --color: #{$haiti};
  }

  &--visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.chatbot-settings-api-key-save {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  flex-shrink: 0;
  height: 32px;
  padding: 0 12px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    border-color: $chatbot-purple;
    color: $chatbot-purple;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.chatbot-settings-select {
  cursor: pointer;
}

.chatbot-settings-size-options {
  display: flex;
  gap: 6px;
}

.chatbot-settings-size-option {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 8px;
  color: $haiti;
  cursor: pointer;
  flex: 1;
  height: 32px;

  &:hover {
    border-color: $chatbot-purple;
    color: $chatbot-purple;
  }

  &--active {
    background-color: $low-grey;
    border-color: $chatbot-purple;
    color: $chatbot-purple;
  }
}

#app textarea.chatbot-composer-input,
.chatbot-element textarea.chatbot-composer-input {
  background-color: $white;
  border: 1px solid $greyscale-semi-light;
  border-radius: 12px;
  color: $haiti;
  box-sizing: border-box;
  display: block;
  font-family: inherit;
  font-size: inherit;
  letter-spacing: normal;
  line-height: inherit;
  max-height: calc(var(--chatbot-line-height, 20px) * 8 + 26px);
  min-height: 48px;
  outline: none;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 12px 48px 12px 14px;
  resize: none;
  width: 100%;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    border: 1px solid $chatbot-purple;
    outline: none;
  }

  &:disabled {
    background-color: $low-grey;
    cursor: not-allowed;
  }

  &--read-only {
    padding-right: 14px;

    &::placeholder {
      color: $greyscale-medium;
      opacity: 1;
      -webkit-text-fill-color: $greyscale-medium;
    }
  }
}

.chatbot-composer-submit {
  align-items: center;
  background-color: $chatbot-purple;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  height: 32px;
  justify-content: center;
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 32px;

  :deep(svg) {
    --color: #{$white};
  }

  &:hover:not(:disabled) {
    background-color: $chatbot-purple-hover;
  }

  &:disabled {
    background-color: $low-grey;
    cursor: not-allowed;

    :deep(svg) {
      --color: #{$greyscale-medium};
    }
  }

  &--stop {
    background-color: $white;
    border: 1px solid $chatbot-purple;

    :deep(svg) {
      --color: #{$chatbot-purple};
    }

    &:hover:not(:disabled) {
      background-color: $low-grey;
    }
  }
}

.chatbot-message-raw {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 640px) {
  .chatbot-element {
    bottom: 16px;
    right: 16px;
  }

  .chatbot-panel {
    height: calc(100vh - #{$app-header-height} - 16px - 72px - 16px);
    right: 0;
    width: calc(100vw - 32px);

    &--expanded {
      border-radius: 16px;
      bottom: 16px;
      height: auto;
      left: 16px;
      right: 16px;
      top: 16px;
      transform: none;
      width: auto;
    }
  }

  .chatbot-settings-menu--sidebar {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
    z-index: 4;
  }

  .chatbot-panel--expanded .chatbot-messages,
  .chatbot-panel--expanded .chatbot-composer {
    max-width: none;
    padding-left: 16px;
    padding-right: 16px;
  }
}

.chatbot-delete-warning {
  align-items: flex-start;
  display: flex;
  gap: 12px;

  :deep(svg) {
    --color: #{$geraldine};
    flex-shrink: 0;
    margin-top: 3px;
  }
}

.chatbot-delete-warning-text {
  color: $geraldine;
  margin: 0;
}
</style>

<i18n src="@/locales/components/elements/chatbot-element.json"></i18n>
